import { useEffect, useState } from "react";
import { toast } from "sonner";
import { CalendarClock, ShieldCheck, MailCheck, Send, Save } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, StatusBadge } from "./ui";

const EVENT_LABELS = {
  threat_detected: "Threat detected (critical / high)",
  threat_resolved: "Threat resolved",
  backup_failed: "Scheduled backup failed",
  weekly_digest: "Weekly digest summary",
};

function nextDue(w) {
  const due = new Date(new Date(w.last_backup).getTime() + (w.backup_frequency_hours || 24) * 3600000);
  const diffH = (due - Date.now()) / 3600000;
  if (diffH <= 0) return { label: "Overdue", overdue: true };
  if (diffH < 1) return { label: `in ${Math.round(diffH * 60)}m`, overdue: false };
  if (diffH < 24) return { label: `in ${Math.round(diffH)}h`, overdue: false };
  return { label: `in ${Math.round(diffH / 24)}d`, overdue: false };
}

export default function Policies() {
  const { user } = useAuth();
  const isAdmin = user.role === "admin";
  const [rows, setRows] = useState([]);
  const [alerts, setAlerts] = useState({ enabled: false, recipient: "" });
  const [testing, setTesting] = useState(false);
  const [settings, setSettings] = useState(null);
  const [recipientsText, setRecipientsText] = useState("");
  const [events, setEvents] = useState({});
  const [saving, setSaving] = useState(false);
  const [digesting, setDigesting] = useState(false);
  const [digestDay, setDigestDay] = useState(0);
  const [digestHour, setDigestHour] = useState(8);

  useEffect(() => {
    api.get("/workloads").then(({ data }) => setRows(data));
    api.get("/alerts/config").then(({ data }) => setAlerts(data));
    if (isAdmin) {
      api.get("/alerts/settings").then(({ data }) => {
        setSettings(data);
        setRecipientsText((data.recipients || []).join(", "));
        setEvents(data.events || {});
        setDigestDay(data.digest_day ?? 0);
        setDigestHour(data.digest_hour ?? 8);
      }).catch(() => {});
    }
  }, [isAdmin]);

  const sendTest = async () => {
    setTesting(true);
    try {
      const { data } = await api.post("/alerts/test");
      if (data.sent) toast.success(`Test alert emailed to ${data.recipient}`);
      else toast.message("Email alerts not configured", { description: "Add a Resend API key to enable outbound alerts." });
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setTesting(false); }
  };

  const savePrefs = async () => {
    const recipients = recipientsText.split(",").map((s) => s.trim()).filter(Boolean);
    if (!recipients.length) { toast.error("Add at least one recipient email"); return; }
    setSaving(true);
    try {
      const { data } = await api.put("/alerts/settings", { recipients, events, digest_day: Number(digestDay), digest_hour: Number(digestHour) });
      setSettings(data);
      setAlerts((a) => ({ ...a, recipient: data.recipients[0] || "", recipients: data.recipients, enabled: true }));
      toast.success("Alert preferences saved");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not save"); }
    finally { setSaving(false); }
  };

  const sendDigest = async () => {
    setDigesting(true);
    try {
      const { data } = await api.post("/alerts/digest/send");
      if (data.sent) toast.success("Weekly digest sent");
      else toast.message("Digest not sent", { description: "Enable the weekly digest event and configure recipients." });
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setDigesting(false); }
  };

  const compliant = rows.filter((w) => !nextDue(w).overdue).length;
  const score = rows.length ? Math.round((compliant / rows.length) * 100) : 100;

  return (
    <div>
      <PageHeader eyebrow="AUTOMATION" title="Backup Policies" subtitle="Schedules, retention and SLA compliance across your fleet">
        {isAdmin && (
          <Button data-testid="send-test-alert" onClick={sendTest} disabled={testing} className="rounded-none bg-secondary hover:bg-primary hover:text-white text-xs">
            <MailCheck className="h-4 w-4 mr-1" /> {testing ? "Sending…" : "Send test alert"}
          </Button>
        )}
      </PageHeader>

      <div className="p-8 space-y-6">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="card-elevated p-6"><span className="text-xs text-muted-foreground uppercase tracking-widest">Policies</span><div className="font-heading text-3xl font-bold tracking-tighter mt-3">{rows.length}</div></div>
          <div className="card-elevated p-6"><span className="text-xs text-muted-foreground uppercase tracking-widest">SLA Compliant</span><div className="font-heading text-3xl font-bold tracking-tighter mt-3 text-[hsl(var(--chart-2))]">{compliant}</div></div>
          <div className="card-elevated p-6"><span className="text-xs text-muted-foreground uppercase tracking-widest">SLA Score</span><div className="font-heading text-3xl font-bold tracking-tighter mt-3">{score}%</div></div>
          <div className="card-elevated p-6">
            <span className="text-xs text-muted-foreground uppercase tracking-widest">Email Alerts</span>
            <div className="font-mono text-sm mt-4 flex items-center gap-2">
              <span className={`h-2 w-2 rounded-full ${alerts.enabled ? "bg-[hsl(var(--chart-2))] glow-green" : "bg-muted-foreground"}`} />
              {alerts.enabled ? "Active" : "Not configured"}
            </div>
          </div>
        </div>

        {isAdmin && settings && (
          <div className="card-elevated rounded-sm" data-testid="alert-preferences">
            <div className="border-b border-border px-6 py-4 flex items-center justify-between">
              <div>
                <h3 className="font-semibold text-sm">Alert Preferences</h3>
                <p className="text-xs text-muted-foreground mt-1">Choose which events email your team, and who receives them. Sender: <span className="font-mono">{settings.sender}</span></p>
              </div>
              <Button data-testid="send-digest-now" variant="outline" onClick={sendDigest} disabled={digesting} className="rounded-none text-xs">
                <Send className="h-4 w-4 mr-1" /> {digesting ? "Sending…" : "Send digest now"}
              </Button>
            </div>
            <div className="p-6 grid md:grid-cols-2 gap-8">
              <div>
                <label className="text-xs text-muted-foreground uppercase tracking-widest">Recipients</label>
                <Input
                  data-testid="alert-recipients-input"
                  value={recipientsText}
                  onChange={(e) => setRecipientsText(e.target.value)}
                  placeholder="alerts@vaultvec.com, security@vaultvec.com"
                  className="rounded-none mt-3 font-mono text-xs"
                />
                <p className="text-[11px] text-muted-foreground mt-2">Comma-separated. All selected event emails go to these addresses.</p>

                <label className="text-xs text-muted-foreground uppercase tracking-widest block mt-6">Weekly digest schedule</label>
                <div className="grid grid-cols-2 gap-3 mt-3">
                  <select
                    data-testid="digest-day-select"
                    value={digestDay}
                    onChange={(e) => setDigestDay(Number(e.target.value))}
                    className="rounded-sm bg-white/5 border border-border text-sm px-3 py-2 focus:border-primary focus:outline-none"
                  >
                    {["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"].map((d, i) => (
                      <option key={d} value={i} className="bg-background">{d}</option>
                    ))}
                  </select>
                  <select
                    data-testid="digest-hour-select"
                    value={digestHour}
                    onChange={(e) => setDigestHour(Number(e.target.value))}
                    className="rounded-sm bg-white/5 border border-border text-sm px-3 py-2 focus:border-primary focus:outline-none"
                  >
                    {Array.from({ length: 24 }, (_, h) => (
                      <option key={h} value={h} className="bg-background">{String(h).padStart(2, "0")}:00 UTC</option>
                    ))}
                  </select>
                </div>
                <p className="text-[11px] text-muted-foreground mt-2">The digest emails on this day &amp; hour each week (only if the weekly digest event is on).</p>
              </div>
              <div className="space-y-3">
                <label className="text-xs text-muted-foreground uppercase tracking-widest">Events</label>
                {Object.keys(EVENT_LABELS).map((key) => (
                  <div key={key} className="flex items-center justify-between border-b border-border/60 pb-2 last:border-0">
                    <span className="text-sm">{EVENT_LABELS[key]}</span>
                    <Switch
                      data-testid={`alert-event-${key}`}
                      checked={!!events[key]}
                      onCheckedChange={(v) => setEvents((prev) => ({ ...prev, [key]: v }))}
                    />
                  </div>
                ))}
              </div>
            </div>
            <div className="border-t border-border px-6 py-4 flex justify-end">
              <Button data-testid="save-alert-prefs" onClick={savePrefs} disabled={saving} className="rounded-none bg-primary hover:bg-primary/90 text-white text-xs">
                <Save className="h-4 w-4 mr-1" /> {saving ? "Saving…" : "Save preferences"}
              </Button>
            </div>
          </div>
        )}

        <div className="card-elevated rounded-sm">
          <table className="w-full text-sm" data-testid="policies-table">
            <thead>
              <tr className="border-b border-border text-left font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
                <th className="px-4 py-3">Workload</th>
                <th className="px-4 py-3">Frequency</th>
                <th className="px-4 py-3">Retention</th>
                <th className="px-4 py-3">Last Backup</th>
                <th className="px-4 py-3">Next Due</th>
                <th className="px-4 py-3">SLA</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((w) => {
                const nd = nextDue(w);
                return (
                  <tr key={w.id} className="border-b border-border last:border-0 hover:bg-secondary/30 transition-colors">
                    <td className="px-4 py-3 font-mono text-xs flex items-center gap-2"><ShieldCheck className="h-3.5 w-3.5 text-[hsl(var(--chart-1))]" /> {w.name}</td>
                    <td className="px-4 py-3 font-mono text-xs text-muted-foreground">every {w.backup_frequency_hours || 24}h</td>
                    <td className="px-4 py-3 font-mono text-xs text-muted-foreground">{w.retention_days || 30}d</td>
                    <td className="px-4 py-3 font-mono text-xs text-muted-foreground">{new Date(w.last_backup).toLocaleString()}</td>
                    <td className="px-4 py-3 font-mono text-xs flex items-center gap-1.5"><CalendarClock className="h-3.5 w-3.5 text-muted-foreground" /><span className={nd.overdue ? "text-primary" : "text-foreground"}>{nd.label}</span></td>
                    <td className="px-4 py-3"><StatusBadge value={nd.overdue ? "at-risk" : "protected"} /></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
